import type { FieldState } from '@colplat/core-client';
import type { PresenceUser, Uuid } from '@colplat/protocol';
import { escapeHtml } from './dom.js';
import { presenceDotHtml } from './presence.js';

/**
 * `state.kind` -> border colour class, state-chip markup, an aria sentence,
 * and whether the control is readonly — mirrors the reference demo's
 * `fieldBlock` state mapping (`docs/reference/collab-field-controls.html`).
 * Colour is never the only signal (hard invariant 11): every state also
 * carries a chip and an aria-label sentence.
 */
export interface StateVisual {
  cls: string;
  chipHtml: string;
  aria: string;
  readOnly: boolean;
  /**
   * The way in when somebody else holds the field: always offered, because
   * losing a field is survivable — whoever had it keeps what they typed
   * (ADR-0003, 2026-09-12; holds no longer lapse, 2026-10-22).
   */
  action?: 'takeOver' | 'takeBack';
  /** The holder, for the "has it" line beneath a kept value. */
  holder?: PresenceUser;
}

/** "just now" / "6 minutes ago" — enough to judge whether to take over. */
function ago(iso: string | undefined): string {
  if (!iso) return '';
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours < 24 ? `${hours} h ago` : `${Math.round(hours / 24)} d ago`;
}

export function describeFieldState(
  state: FieldState,
  resolveUser: (id: Uuid) => PresenceUser | undefined,
): StateVisual {
  switch (state.kind) {
    case 'system':
      return { cls: 'is-system', chipHtml: '', aria: 'Set by the system, not editable', readOnly: true };
    case 'locked':
    case 'takenOffYou': {
      const holder = resolveUser(state.holder);
      const name = holder ? escapeHtml(holder.name) : 'another user';
      const dot = holder ? presenceDotHtml(holder, 13) : '';
      const when = ago(state.since);
      // Connected: they are in it now. Not connected: an unfinished edit —
      // a different glyph, not a paler lock (invariant #11).
      const chipHtml = state.online
        ? `${dot}<span class="cf-txt">${name}</span><span aria-hidden="true">🔒</span>`
        : `${dot}<span class="cf-txt">${name} · unfinished</span><span aria-hidden="true">✎</span>`;
      const doing = state.online
        ? `${name} is editing this field`
        : `${name} started editing this field${when ? ` ${when}` : ''} and hasn't finished`;
      if (state.kind === 'takenOffYou') {
        return {
          cls: 'is-stolen', chipHtml, readOnly: true, action: 'takeBack', ...(holder ? { holder } : {}),
          aria: `Read-only. ${name} took this field over. Your unsaved value is kept below`,
        };
      }
      return {
        cls: 'is-locked', chipHtml, readOnly: true, action: 'takeOver', ...(holder ? { holder } : {}),
        aria: `Read-only. ${doing}`,
      };
    }
    case 'mine':
      return {
        cls: 'is-mine',
        chipHtml: '<span aria-hidden="true">✏️</span><span class="cf-txt">yours</span>',
        aria: 'You hold this field. Others see it as locked',
        readOnly: false,
      };
    case 'claiming':
      return {
        cls: 'is-pending',
        chipHtml:
          '<span class="spinner-border" style="width:.55rem;height:.55rem;border-width:.1rem" ' +
          'aria-hidden="true"></span><span class="cf-txt">claiming</span>',
        aria: 'Claiming this field',
        readOnly: false,
      };
    case 'needsReview':
      return {
        cls: 'is-conflict',
        chipHtml: '<span aria-hidden="true">⚠</span><span class="cf-txt">review</span>',
        aria: 'Two versions to reconcile — open history to choose',
        readOnly: false,
      };
    case 'remotelyChanged':
      return {
        cls: 'is-remote',
        chipHtml: '<span class="cf-txt">changed</span>',
        aria: 'Just changed remotely',
        readOnly: false,
      };
    case 'idle':
      return { cls: 'is-idle', chipHtml: '', aria: '', readOnly: false };
  }
}
